import { StandardEvents } from '@shopify/events';

/**
 * Shows the material entry for the selected variant and hides tabs whose
 * entries are all empty. Listens for the same product-select event as the
 * variant picker.
 */
class ProductMaterials extends HTMLElement {
  #picker = null;
  #variants = [];

  connectedCallback() {
    this.tablist = this.querySelector('[role="tablist"]');
    this.#picker = this.querySelector('[data-materials-picker]');
    this.#variants = this.#readVariants();
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.#picker?.addEventListener('change', this.#onPickerChange);
    this.#ensureSelectedTab();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#picker?.removeEventListener('change', this.#onPickerChange);
  }

  #readVariants() {
    const node = this.querySelector('[data-materials-variants]');
    if (!node) return [];
    try {
      const variants = JSON.parse(node.textContent || '[]');
      return Array.isArray(variants) ? variants : [];
    } catch {
      return [];
    }
  }

  #pickerValues() {
    const selected = {};
    this.#picker?.querySelectorAll('select').forEach((select) => {
      selected[select.dataset.optionIndex] = select.value;
    });
    return selected;
  }

  #variantIdForPicker() {
    const selected = this.#pickerValues();
    const entries = Object.entries(selected);
    if (!entries.length || !this.#variants.length) return '';

    const matches = (variant, index, value) => variant.options[Number(index)] === value;
    const exact = this.#variants.find((variant) => entries.every(([index, value]) => matches(variant, index, value)));
    if (exact) return exact.id;

    let best = null;
    let bestScore = -1;
    this.#variants.forEach((variant) => {
      const score = entries.reduce((total, [index, value]) => total + (matches(variant, index, value) ? 1 : 0), 0);
      if (score > bestScore) {
        best = variant;
        bestScore = score;
      }
    });
    return best?.id || '';
  }

  #syncPicker(variantId) {
    if (!this.#picker) return;
    const variant = this.#variants.find((item) => this.#idsMatch(item.id, variantId));
    if (!variant) return;

    variant.options.forEach((option, index) => {
      const select = this.#picker.querySelector(`select[data-option-index="${index}"]`);
      if (select && [...select.options].some((item) => item.value === option)) select.value = option;
    });
  }

  #onPickerChange = () => {
    const variantId = this.#variantIdForPicker();
    if (variantId) this.#showVariant(variantId);
  };

  /**
   * @param {string | number | null | undefined} value
   */
  #normalizeId(value) {
    if (value == null || value === '') return '';
    return String(value).split('/').pop() || '';
  }

  /**
   * @param {string | number | null | undefined} left
   * @param {string | number | null | undefined} right
   */
  #idsMatch(left, right) {
    const a = this.#normalizeId(left);
    const b = this.#normalizeId(right);
    return a !== '' && a === b;
  }

  /**
   * @param {Event & { product?: { id?: string }, detail?: { variantId?: string }, promise?: Promise<{ detail?: { productId?: string, resource?: { id?: string | number }, newProduct?: object }, variant?: { id?: string } }> }} event
   */
  #onProductSelect = (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('product-card, quick-add-dialog')) return;

    const eventProductId = event.product?.id;
    if (eventProductId && !this.#idsMatch(eventProductId, this.dataset.productId)) return;

    const immediateId = event.detail?.variantId;
    if (immediateId) this.#showVariant(immediateId);

    event.promise
      ?.then(({ detail, variant }) => {
        if (detail?.newProduct) return;
        if (detail?.productId && !this.#idsMatch(detail.productId, this.dataset.productId)) return;

        const variantId = detail?.resource?.id || variant?.id;
        if (variantId != null && variantId !== '') this.#showVariant(variantId);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-materials] Event promise rejected:', error);
      });
  };

  /**
   * @param {string | number} variantId
   */
  #showVariant(variantId) {
    this.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
      let any = false;
      panel.querySelectorAll('[data-variant-id], [data-variant-ids], [data-product-level]').forEach((entry) => {
        if (entry.hasAttribute('data-product-level')) {
          entry.hidden = false;
          any = true;
          return;
        }

        const idList = entry.getAttribute('data-variant-ids');
        const match = idList
          ? idList.split(',').some((id) => this.#idsMatch(id, variantId))
          : this.#idsMatch(entry.getAttribute('data-variant-id'), variantId);
        entry.hidden = !match;
        if (match) any = true;
      });

      const tab = this.#tabFor(panel);
      if (tab) tab.hidden = !any;
      if (!any) panel.hidden = true;
    });

    this.#syncPicker(variantId);
    this.#ensureSelectedTab();
  }

  #ensureSelectedTab() {
    const visible = this.#visibleTabs();
    if (this.tablist) this.tablist.hidden = visible.length < 2;

    const selected = visible.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (selected) {
      this.#select(selected);
    } else if (visible[0]) {
      this.#select(visible[0]);
    }

    this.#syncSection(visible.length > 0);
  }

  /**
   * @param {boolean} hasTabs
   */
  #syncSection(hasTabs) {
    const section = this.closest('.shopify-section');
    if (!section || window.Shopify?.designMode) return;
    section.hidden = !hasTabs;
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  #visibleTabs() {
    return /** @type {HTMLButtonElement[]} */ (
      [...this.querySelectorAll('[role="tab"]')].filter((tab) => !tab.hidden)
    );
  }

  /**
   * @param {HTMLElement} panel
   */
  #tabFor(panel) {
    const id = panel.getAttribute('aria-labelledby');
    if (!id) return null;
    return this.querySelector(`#${CSS.escape(id)}`);
  }

  /**
   * @param {HTMLElement} tab
   */
  #panelFor(tab) {
    const id = tab.getAttribute('aria-controls');
    if (!id) return null;
    return this.querySelector(`#${CSS.escape(id)}`);
  }

  /**
   * @param {HTMLButtonElement} tab
   */
  #select(tab) {
    this.querySelectorAll('[role="tab"]').forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.tabIndex = selected ? 0 : -1;
      if (!selected) {
        const panel = this.#panelFor(item);
        if (panel) panel.hidden = true;
      }
    });

    const panel = this.#panelFor(tab);
    if (panel) {
      this.#placePicker(panel);
      panel.hidden = false;
    }
  }

  /**
   * @param {HTMLElement} entry
   * @param {string | number} variantId
   */
  #entryMatches(entry, variantId) {
    const idList = entry.getAttribute('data-variant-ids');
    return idList
      ? idList.split(',').some((id) => this.#idsMatch(id, variantId))
      : this.#idsMatch(entry.getAttribute('data-variant-id'), variantId);
  }

  /**
   * Option indexes whose value changes which entry this panel shows.
   * @param {HTMLElement} panel
   */
  #optionsThatChange(panel) {
    const indexes = new Set();
    const entries = [...panel.querySelectorAll('[data-variant-id], [data-variant-ids]')];
    if (!entries.length || !this.#picker) return indexes;

    this.#picker.querySelectorAll('select').forEach((select) => {
      const index = Number(select.dataset.optionIndex);
      const signatures = new Set();
      this.#variants.forEach((variant) => {
        const value = variant.options[index];
        const entry = entries.find((node) => this.#entryMatches(node, variant.id));
        const signature = entry
          ? entry.getAttribute('data-variant-ids') || entry.getAttribute('data-variant-id')
          : '';
        signatures.add(`${value}\u0000${signature}`);
      });

      const byValue = new Map();
      let changes = false;
      signatures.forEach((token) => {
        const splitAt = token.indexOf('\u0000');
        const value = token.slice(0, splitAt);
        const signature = token.slice(splitAt + 1);
        if (!byValue.has(value)) {
          byValue.set(value, signature);
        } else if (byValue.get(value) !== signature) {
          byValue.set(value, '*');
        }
      });
      const outcomes = new Set(byValue.values());
      if (outcomes.size > 1) changes = true;
      if (changes) indexes.add(index);
    });

    return indexes;
  }

  /**
   * @param {HTMLElement} panel
   */
  #placePicker(panel) {
    if (!this.#picker) return;
    const indexes = this.#optionsThatChange(panel);
    this.#picker.querySelectorAll('select').forEach((select) => {
      const option = select.closest('.product-materials__picker-option');
      if (option) option.hidden = !indexes.has(Number(select.dataset.optionIndex));
    });
    this.#picker.hidden = indexes.size === 0;
    if (indexes.size > 0) panel.prepend(this.#picker);
  }

  /**
   * @param {Event} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const tab = target.closest('[role="tab"]');
    if (!(tab instanceof HTMLButtonElement) || !this.contains(tab) || tab.hidden) return;
    this.#select(tab);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLButtonElement) || target.getAttribute('role') !== 'tab' || target.hidden) return;

    const visible = this.#visibleTabs();
    const currentIndex = visible.indexOf(target);
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % visible.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + visible.length) % visible.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = visible.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const next = visible[nextIndex];
    if (!next) return;
    this.#select(next);
    next.focus();
  };
}

if (!customElements.get('product-materials')) {
  customElements.define('product-materials', ProductMaterials);
}
